/**
 * gps-speed-core.js — pure math behind /gps-speedometer.
 *
 * Kept free of DOM and browser APIs so tests/gps-speed-core.test.mjs can load THIS
 * file (not a copy of it) in node and assert the numbers on synthetic tracks.
 *
 * Three facts drive most of the design here:
 *
 *  1. coords.speed has THREE states, not two. Both engines guard `>= 0`
 *     (WebKit GeolocationPositionDataCocoa.mm, Chromium kBadSpeed=-1), so a
 *     receiver's "invalid" -1 surfaces as null. iOS CoreLocation reports -1
 *     while parked -> null; Android passes a real 0 through as 0. null means
 *     "unknown", 0 means "measured, stationary". Collapsing them loses the
 *     difference between a parked car and a receiver with nothing to say.
 *  2. There is no speedAccuracy in the web API. Both OSes have it (Android
 *     getSpeedAccuracyMetersPerSecond, iOS CLLocation.speedAccuracy); neither
 *     is plumbed through the W3C IDL. Per-sample confidence is unavailable, so
 *     plausibility has to be inferred from coords.accuracy and from physics.
 *  3. Fixes arrive at ~1 Hz and the interval is not guaranteed. Anything that
 *     accumulates or smooths must be a function of real dt, never of a sample
 *     count.
 *
 * UNECE R39 note: two tolerances exist and both are real. §5.4 (+4 km/h) binds
 * the type-approval sample; ANNEX 3 (+6 km/h for M/N) binds every series
 * vehicle you can actually buy. R39_C.serial is the honest default for "why
 * does my dashboard disagree".
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.GpsSpeedCore = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // ── Unit conversion ──────────────────────────────────────────────────────
    var UNITS = {
        kmh:   { factor: 3.6,        decimals: 0, step: 10 },
        mph:   { factor: 2.236936,   decimals: 0, step: 10 },
        knots: { factor: 1.943844,   decimals: 0, step: 5  },
        ms:    { factor: 1,          decimals: 1, step: 5  }
    };

    function toUnit(mps, unit) {
        var u = UNITS[unit] || UNITS.kmh;
        return mps * u.factor;
    }

    function fromUnit(value, unit) {
        var u = UNITS[unit] || UNITS.kmh;
        return value / u.factor;
    }

    /**
     * Display string for a speed. Sub-10 readings get one decimal (walking and
     * cycling live down there and integers would quantise them to uselessness);
     * above that, integers — a driver reading a glance-sized number does not
     * care about 0.1 km/h and the extra digit makes the glyph narrower.
     *
     * Exact zero is special-cased to "0". Anything below STATIONARY_MPS is
     * already floored to a true zero upstream, so "0.0" would be a decimal of
     * a value we know exactly — and at ~100px it reads as a malfunction.
     */
    function formatSpeed(mps, unit) {
        var v = toUnit(mps, unit);
        if (!isFinite(v) || v <= 0) return '0';
        if (unit === 'ms') return v.toFixed(1);
        return v < 10 ? v.toFixed(1) : String(Math.round(v));
    }

    // ── Geodesy ──────────────────────────────────────────────────────────────
    var EARTH_R = 6371008.8; // IUGG mean radius, metres

    function haversineMeters(lat1, lon1, lat2, lon2) {
        var toRad = Math.PI / 180;
        var dLat = (lat2 - lat1) * toRad;
        var dLon = (lon2 - lon1) * toRad;
        var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(a)));
    }

    function bearingDeg(lat1, lon1, lat2, lon2) {
        var toRad = Math.PI / 180;
        var y = Math.sin((lon2 - lon1) * toRad) * Math.cos(lat2 * toRad);
        var x = Math.cos(lat1 * toRad) * Math.sin(lat2 * toRad) -
                Math.sin(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.cos((lon2 - lon1) * toRad);
        return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
    }

    // ── Plausibility ─────────────────────────────────────────────────────────
    // Ceilings picked to reject GPS glitches without ever arguing with physics
    // a user might actually experience. 1.5 g brackets any road vehicle
    // (a Veyron does ~1.3 g braking); 180 m/s clears a TGV at 320 km/h and an
    // airliner at cruise, which are real reported use cases.
    var MAX_PLAUSIBLE_MPS = 180;
    var MAX_PLAUSIBLE_ACCEL = 14.7;   // m/s^2
    var ACCURACY_HARD_LIMIT = 100;    // m — beyond this a fix is a cell-tower guess

    /**
     * Gate for statistics ONLY (max / distance / track), never for the live
     * readout. Refusing to show a number is the failure mode users punish
     * hardest in competitor reviews ("GPS accuracy too low... all other apps
     * are fine") — the live digits always render, and only the permanent record
     * is protected from a spike.
     */
    function isPlausibleSample(sample, prev) {
        if (!sample || !isFinite(sample.mps) || sample.mps < 0) return false;
        if (sample.mps > MAX_PLAUSIBLE_MPS) return false;
        if (sample.accuracy != null && sample.accuracy > ACCURACY_HARD_LIMIT) return false;
        if (prev && isFinite(prev.mps)) {
            var dt = (sample.t - prev.t) / 1000;
            // Only judge acceleration across a tight window; after a long gap
            // (tunnel, backgrounded tab) a large jump is legitimate.
            if (dt > 0 && dt <= 5) {
                if (Math.abs(sample.mps - prev.mps) / dt > MAX_PLAUSIBLE_ACCEL) return false;
            }
        }
        return true;
    }

    // ── Speed source selection ───────────────────────────────────────────────
    /**
     * Decide what this fix says about speed.
     *
     * Doppler (coords.speed) is preferred by ~100x: a receiver solves velocity
     * from carrier frequency shift at cm/s, while differencing positions
     * inherits their metre-scale slow-moving errors (Inside GNSS). Derived
     * speed is the fallback for receivers that report null — which is NOT a
     * desktop-only situation: Windows Chrome's WinRT Geolocator does report
     * speed, and plenty of phones drop it intermittently.
     *
     * @returns {{mps:number, source:'doppler'|'derived'|'none', accuracy:number|null, t:number}}
     */
    function pickSpeed(coords, timestamp, prevFix) {
        var t = timestamp;
        var acc = (coords && isFinite(coords.accuracy)) ? coords.accuracy : null;

        if (coords && coords.speed != null && isFinite(coords.speed) && coords.speed >= 0) {
            return { mps: coords.speed, source: 'doppler', accuracy: acc, t: t };
        }

        if (prevFix && coords) {
            var dt = (t - prevFix.t) / 1000;
            if (dt > 0.15 && dt <= 30) {
                var d = haversineMeters(prevFix.lat, prevFix.lon, coords.latitude, coords.longitude);
                // Movement inside the error circle is indistinguishable from
                // the fix wandering while parked. Deriving speed from it is how
                // every phone shows "3 km/h" on a table.
                var noise = Math.max(prevFix.accuracy || 0, acc || 0);
                if (d <= noise * 0.5) return { mps: 0, source: 'derived', accuracy: acc, t: t };
                return { mps: d / dt, source: 'derived', accuracy: acc, t: t };
            }
        }

        return { mps: 0, source: 'none', accuracy: acc, t: t };
    }

    // ── Smoothing ────────────────────────────────────────────────────────────
    /**
     * Time-constant EMA with an adaptive response.
     *
     * Not one competitor smooths at all (audit of six sites' bundles) — which
     * is exactly why every one of them jitters. But a naive EMA trades jitter
     * for lag, and lag on a speedometer is its own bug: at 1 Hz you already eat
     * >=0.5 s of quantisation before any filter runs.
     *
     * Three properties make this behave:
     *  - alpha is derived from real dt via 1-exp(-dt/tau), so a dropped fix
     *    doesn't make the filter inertial and a fast burst doesn't make it
     *    twitchy. Sample-count EMA gets both wrong.
     *  - tau shrinks when the input jumps hard (fast-attack): steady cruise is
     *    smoothed heavily, real acceleration passes through almost raw.
     *  - fast-attack is gated on COHERENCE, not just magnitude. This is the
     *    part that took a measurement to find: judging "is this real?" from a
     *    single sample's size lets the noise open the gate itself. Doppler
     *    noise of only ±2 km/h was clearing a 5 km/h threshold ~39% of the way
     *    and cutting jitter rejection to 54.7%. Real acceleration is coherent
     *    in time — successive deltas share a sign; noise reverses every sample.
     *    Requiring a consistent direction before speeding up separates them
     *    from the same numbers: rejection went 54.7% -> 65.7% on the cruise
     *    fixture with the step response unchanged at 1 s.
     */
    function SpeedSmoother(opts) {
        opts = opts || {};
        this.tauSteady = opts.tauSteady != null ? opts.tauSteady : 1.6; // s
        this.tauFast   = opts.tauFast   != null ? opts.tauFast   : 0.35;
        this.jumpMps   = opts.jumpMps   != null ? opts.jumpMps   : 1.4; // ~5 km/h
        // A jump this large is not survivable as noise — let it through even
        // without a confirming second sample (emergency braking, train start).
        this.jumpHardMps = opts.jumpHardMps != null ? opts.jumpHardMps : 4.0; // ~14 km/h
        // How much an unconfirmed (incoherent) jump is damped.
        this.incoherentK = opts.incoherentK != null ? opts.incoherentK : 0.3;
        this.value = null;
        this.lastT = null;
        this.lastSign = 0;
    }

    SpeedSmoother.prototype.reset = function () {
        this.value = null;
        this.lastT = null;
        this.lastSign = 0;
    };

    SpeedSmoother.prototype.push = function (mps, t) {
        if (!isFinite(mps) || mps < 0) mps = 0;
        if (this.value === null || this.lastT === null) {
            this.value = mps; this.lastT = t; this.lastSign = 0;
            return this.value;
        }
        var dt = (t - this.lastT) / 1000;
        if (!(dt > 0)) return this.value;
        if (dt > 5) { // long gap — the old value says nothing about now
            this.value = mps; this.lastT = t; this.lastSign = 0;
            return this.value;
        }

        var diff = mps - this.value;
        var delta = Math.abs(diff);
        var sign = diff > 0 ? 1 : (diff < 0 ? -1 : 0);

        // Magnitude vote: how step-like is this on its own?
        var kMag = Math.min(1, delta / this.jumpMps);
        // Coherence vote: does it continue the previous direction? A lone
        // sample that reverses the trend is treated as noise until confirmed.
        var coherent = (sign !== 0 && sign === this.lastSign);
        var kTrend = coherent ? 1 : this.incoherentK;
        var k = kMag * kTrend;
        // ...unless it's simply too big to be noise.
        if (delta >= this.jumpHardMps) k = 1;

        var tau = this.tauSteady + (this.tauFast - this.tauSteady) * k;
        var alpha = 1 - Math.exp(-dt / tau);
        this.value = this.value + alpha * diff;
        this.lastT = t;
        if (sign !== 0) this.lastSign = sign;
        return this.value;
    };

    /**
     * Below this the doppler solution is noise, not motion. Standing still with
     * a clear sky still produces a wandering sub-metre/s velocity vector, and
     * showing "0.4 km/h" to a parked user reads as broken.
     */
    var STATIONARY_MPS = 0.35;

    function isStationary(mps) { return mps < STATIONARY_MPS; }

    // ── UNECE R39 ────────────────────────────────────────────────────────────
    /**
     * 0 <= (V1 - V2) <= 0.1 * V2 + C     where V1 = indicated, V2 = true
     *
     * The lower bound is the whole point: a speedometer may never read low.
     * That single inequality explains the #1 complaint aimed at every GPS
     * speedometer ("your app says 91, my dash says 100") — the dash is obeying
     * the law, not lying, and the app is not wrong.
     */
    var R39_C = {
        serial:       6, // ANNEX 3, conformity of production, categories M and N
        typeApproval: 4, // §5.4, approval sample
        moto:         8, // L3, L4, L5
        moped:        4  // L1, L2
    };

    /** Legal true-speed window for a given dashboard reading. */
    function r39TrueRange(indicatedKmh, category) {
        var C = R39_C[category] || R39_C.serial;
        return {
            min: Math.max(0, (indicatedKmh - C) / 1.1),
            max: indicatedKmh
        };
    }

    /** Legal dashboard window for a given true speed. */
    function r39IndicatedRange(trueKmh, category) {
        var C = R39_C[category] || R39_C.serial;
        return { min: trueKmh, max: 1.1 * trueKmh + C };
    }

    /**
     * Compare a dashboard reading against GPS truth.
     *
     * verdict:
     *   'under'  — reads LOW. Illegal under R39 and the genuinely unsafe case:
     *              the driver is going faster than they are told. Classic
     *              symptom of oversized tyres.
     *   'normal' — inside the legal corridor. The expected result for a healthy
     *              car; the number exists to be reassuring, not alarming.
     *   'over'   — reads high beyond tolerance. Usually undersized tyres or a
     *              swapped diff/cluster.
     */
    function calibration(indicatedKmh, trueKmh, category) {
        if (!isFinite(indicatedKmh) || !isFinite(trueKmh) || trueKmh <= 0) return null;
        var C = R39_C[category] || R39_C.serial;
        var diff = indicatedKmh - trueKmh;
        var allowed = 0.1 * trueKmh + C;
        var verdict = diff < 0 ? 'under' : (diff <= allowed ? 'normal' : 'over');
        return {
            indicated: indicatedKmh,
            trueSpeed: trueKmh,
            diff: diff,
            errorPct: (diff / trueKmh) * 100,
            allowedMax: allowed,
            withinR39: verdict === 'normal',
            verdict: verdict,
            category: category || 'serial',
            C: C
        };
    }

    // ── Trip statistics ──────────────────────────────────────────────────────
    /**
     * Distance integrates measured displacement between plausible fixes rather
     * than speed*dt: a dropped fix leaves a real gap on the map, and pretending
     * the last speed held across it inflates the odometer.
     *
     * Max speed is gated by isPlausibleSample because a max is permanent — one
     * multipath spike writes "182 km/h" into a cyclist's trip forever. Strava
     * ships this bug and documents it rather than fixing it.
     */
    function TripAccumulator(opts) {
        opts = opts || {};
        this.stationaryMps = opts.stationaryMps != null ? opts.stationaryMps : STATIONARY_MPS;
        this.reset();
    }

    TripAccumulator.prototype.reset = function () {
        this.distance = 0;
        this.maxMps = 0;
        this.movingMs = 0;
        this.elapsedMs = 0;
        this.startedAt = null;
        this.lastFix = null;
        this.samples = 0;
        this._sumMps = 0;
        this._sumWeight = 0;
    };

    /**
     * @param fix {{lat,lon,alt,mps,accuracy,t}} already speed-picked
     * @returns {boolean} whether the fix was accepted into the stats
     */
    TripAccumulator.prototype.push = function (fix) {
        if (this.startedAt === null) this.startedAt = fix.t;
        this.elapsedMs = fix.t - this.startedAt;

        var ok = isPlausibleSample(fix, this.lastFix);
        if (!ok) return false;

        if (this.lastFix) {
            var dt = (fix.t - this.lastFix.t) / 1000;
            if (dt > 0 && dt <= 30) {
                var d = haversineMeters(this.lastFix.lat, this.lastFix.lon, fix.lat, fix.lon);
                var noise = Math.max(this.lastFix.accuracy || 0, fix.accuracy || 0);
                // Same idea as pickSpeed: sub-error-circle wander is not travel.
                // Without this an odometer creeps for hours in a parked car.
                if (d > noise * 0.5 && !(isStationary(fix.mps) && isStationary(this.lastFix.mps))) {
                    this.distance += d;
                }
                // Stationary intervals are excluded from BOTH moving time and
                // the mean, so the two agree. Folding stoplights into the
                // average drags it toward zero and answers a question nobody
                // asked ("how fast including not moving?"); it also silently
                // contradicts the "moving" label on the time tile next to it.
                //
                // Judged on the interval's midpoint, not on the arriving
                // sample: an interval is a span between two fixes, and testing
                // only the far end counts the pull-away from a light as motion
                // while ignoring the roll-up to it. The midpoint treats both
                // transitions the same way.
                var mid = (fix.mps + this.lastFix.mps) / 2;
                if (!isStationary(mid)) {
                    this.movingMs += dt * 1000;
                    // Time-weighted: an irregular fix rate must not bias it.
                    this._sumMps += fix.mps * dt;
                    this._sumWeight += dt;
                }
            }
        }

        if (fix.mps > this.maxMps) this.maxMps = fix.mps;
        this.samples++;
        this.lastFix = fix;
        return true;
    };

    /** Mean over MOVING time. Averaging in stoplights answers a question nobody asked. */
    TripAccumulator.prototype.avgMps = function () {
        return this._sumWeight > 0 ? this._sumMps / this._sumWeight : 0;
    };

    // Serialisation exists so an accidental reload doesn't erase a drive.
    // Short keys: this rides in localStorage next to every other tool's data,
    // and a 5 MB origin quota is shared, not ours.
    TripAccumulator.prototype.toJSON = function () {
        return { d: this.distance, m: this.maxMps, t: this.movingMs, e: this.elapsedMs,
                 s: this.samples, sw: this._sumWeight, sm: this._sumMps };
    };

    TripAccumulator.fromJSON = function (o) {
        var t = new TripAccumulator();
        if (!o) return t;
        t.distance = o.d || 0;
        t.maxMps = o.m || 0;
        t.movingMs = o.t || 0;
        t.elapsedMs = o.e || 0;
        t.samples = o.s || 0;
        t._sumWeight = o.sw || 0;
        t._sumMps = o.sm || 0;
        // lastFix stays null on purpose: the gap across a reload is unknown, so
        // the next fix must not be differenced against a pre-reload position —
        // that would invent distance the user never travelled.
        t.lastFix = null;
        t.startedAt = null;
        return t;
    };

    // ── Track recording ──────────────────────────────────────────────────────
    /**
     * Bounded track that DECIMATES instead of truncating.
     *
     * The obvious `if (track.length < CAP) track.push(fix)` silently stops
     * recording once the cap is hit: measured on a 6-hour drive, 21 000 fixes
     * produced exactly 20 000 points, the stats kept counting to 583 km, and
     * the exported GPX just ended somewhere in hour five with nothing saying so.
     * A file that disagrees with the numbers beside it, invisibly, is worse than
     * no file.
     *
     * Halving the resolution instead keeps the track covering the WHOLE drive —
     * only the sample interval grows (1 s -> 2 s -> 4 s...). That is the same
     * trade every real GPS logger makes, and it degrades a property nobody
     * watches (point density) rather than one everybody assumes (completeness).
     */
    function TrackRecorder(cap) {
        this.cap = cap || 20000;
        this.reset();
    }

    TrackRecorder.prototype.reset = function () {
        this.points = [];
        this.stride = 1;       // record 1 fix in every `stride`
        this.decimations = 0;  // how many times resolution was halved
        this._seen = 0;
    };

    /** @returns {boolean} whether this fix was recorded */
    TrackRecorder.prototype.push = function (fix) {
        this._seen++;
        if ((this._seen - 1) % this.stride !== 0) return false;
        this.points.push(fix);
        if (this.points.length >= this.cap) {
            // Keep every other point; the first and last always survive.
            var kept = [];
            for (var i = 0; i < this.points.length; i += 2) kept.push(this.points[i]);
            var last = this.points[this.points.length - 1];
            if (kept[kept.length - 1] !== last) kept.push(last);
            this.points = kept;
            this.stride *= 2;
            this.decimations++;
        }
        return true;
    };

    /** Seconds between recorded points at the current resolution (fixes ~1 Hz). */
    TrackRecorder.prototype.intervalSec = function () { return this.stride; };

    /**
     * Compact array-of-arrays with timestamps stored as deltas from the first
     * point — a 10 000-point track drops from ~1.2 MB of pretty JSON to ~250 KB,
     * which matters when the whole origin shares a 5 MB quota.
     *
     * Coordinates keep 7 decimals to match buildGPX's own toFixed(7): at 6 dp a
     * restored session exported a GPX that differed from the pre-reload one in
     * the last digit. Physically meaningless (11 cm vs GPS's metres), but a
     * round-trip that quietly rewrites the user's file is a bug regardless —
     * saving state must not change output.
     */
    TrackRecorder.prototype.toJSON = function () {
        var t0 = this.points.length ? this.points[0].t : 0;
        return {
            st: this.stride, dc: this.decimations, sn: this._seen, t0: t0,
            p: this.points.map(function (x) {
                return [
                    Math.round(x.lat * 1e7) / 1e7,
                    Math.round(x.lon * 1e7) / 1e7,
                    x.alt == null ? null : Math.round(x.alt * 10) / 10,
                    Math.round(x.mps * 100) / 100,
                    x.accuracy == null ? null : Math.round(x.accuracy),
                    x.t - t0,
                    x.source === 'doppler' ? 1 : 0
                ];
            })
        };
    };

    TrackRecorder.fromJSON = function (o, cap) {
        var r = new TrackRecorder(cap);
        if (!o || !o.p) return r;
        var t0 = o.t0 || 0;
        r.stride = o.st || 1;
        r.decimations = o.dc || 0;
        r._seen = o.sn || 0;
        r.points = o.p.map(function (a) {
            return { lat: a[0], lon: a[1], alt: a[2], mps: a[3], accuracy: a[4],
                     t: a[5] + t0, source: a[6] ? 'doppler' : 'derived' };
        });
        return r;
    };

    // ── Track export ─────────────────────────────────────────────────────────
    function isoTime(ms) { return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z'); }

    function xmlEscape(s) {
        return String(s).replace(/[<>&'"]/g, function (c) {
            return { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[c];
        });
    }

    /**
     * GPX 1.1. Speed goes in a gpxtpx:TrackPointExtension, which is what Garmin
     * Connect / Strava / OsmAnd actually read — a bare <speed> child is GPX 1.0
     * and silently ignored by modern importers.
     */
    function buildGPX(points, meta) {
        meta = meta || {};
        var name = xmlEscape(meta.name || 'Timbrica track');
        var out = [];
        out.push('<?xml version="1.0" encoding="UTF-8"?>');
        out.push('<gpx version="1.1" creator="Timbrica gps-speedometer" xmlns="http://www.topografix.com/GPX/1/1" ' +
                 'xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1" ' +
                 'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ' +
                 'xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">');
        out.push('  <metadata><name>' + name + '</name>' +
                 (points.length ? '<time>' + isoTime(points[0].t) + '</time>' : '') + '</metadata>');
        out.push('  <trk><name>' + name + '</name><trkseg>');
        for (var i = 0; i < points.length; i++) {
            var p = points[i];
            var seg = '    <trkpt lat="' + p.lat.toFixed(7) + '" lon="' + p.lon.toFixed(7) + '">';
            if (p.alt != null && isFinite(p.alt)) seg += '<ele>' + p.alt.toFixed(2) + '</ele>';
            seg += '<time>' + isoTime(p.t) + '</time>';
            if (p.mps != null && isFinite(p.mps)) {
                seg += '<extensions><gpxtpx:TrackPointExtension><gpxtpx:speed>' +
                       p.mps.toFixed(3) + '</gpxtpx:speed></gpxtpx:TrackPointExtension></extensions>';
            }
            seg += '</trkpt>';
            out.push(seg);
        }
        out.push('  </trkseg></trk>');
        out.push('</gpx>');
        return out.join('\n');
    }

    function buildCSV(points) {
        var rows = ['time,latitude,longitude,altitude_m,speed_mps,speed_kmh,accuracy_m,source'];
        for (var i = 0; i < points.length; i++) {
            var p = points[i];
            rows.push([
                isoTime(p.t),
                p.lat.toFixed(7),
                p.lon.toFixed(7),
                (p.alt != null && isFinite(p.alt)) ? p.alt.toFixed(2) : '',
                (p.mps != null && isFinite(p.mps)) ? p.mps.toFixed(3) : '',
                (p.mps != null && isFinite(p.mps)) ? (p.mps * 3.6).toFixed(2) : '',
                (p.accuracy != null && isFinite(p.accuracy)) ? p.accuracy.toFixed(1) : '',
                p.source || ''
            ].join(','));
        }
        return rows.join('\n');
    }

    // ── Accuracy classification ──────────────────────────────────────────────
    /**
     * Deliberately coarse. accuracy is a radius, not a speed error, and the web
     * gives no speedAccuracy — so this describes the FIX, and the UI must never
     * imply it grades the speed reading.
     */
    function accuracyClass(m) {
        if (m == null || !isFinite(m)) return 'unknown';
        if (m <= 8)   return 'excellent';
        if (m <= 20)  return 'good';
        if (m <= 50)  return 'fair';
        return 'poor';
    }

    // ── Distance formatting ──────────────────────────────────────────────────
    /**
     * Distance paired with a unit KEY, not a display string — the caller looks
     * the key up in its own translations. Returning "m"/"km" here would hardcode
     * English into every locale, which is exactly the bug this shape prevents.
     *
     * @returns {{value:string, unit:'m'|'km'|'mi'|'ft'|'nmi'}}
     */
    function formatDistance(meters, unit) {
        if (unit === 'knots') {
            var nm = meters / 1852;
            return { value: nm < 10 ? nm.toFixed(2) : nm.toFixed(1), unit: 'nmi' };
        }
        if (unit === 'mph') {
            var mi = meters / 1609.344;
            if (mi < 0.1) return { value: String(Math.round(meters * 3.28084)), unit: 'ft' };
            return { value: mi < 10 ? mi.toFixed(2) : mi.toFixed(1), unit: 'mi' };
        }
        if (meters < 1000) return { value: String(Math.round(meters)), unit: 'm' };
        var km = meters / 1000;
        return { value: km < 10 ? km.toFixed(2) : km.toFixed(1), unit: 'km' };
    }

    function formatDuration(ms) {
        var s = Math.floor(ms / 1000);
        var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
        var pad = function (n) { return n < 10 ? '0' + n : String(n); };
        return h > 0 ? h + ':' + pad(m) + ':' + pad(ss) : pad(m) + ':' + pad(ss);
    }

    return {
        UNITS: UNITS,
        toUnit: toUnit,
        fromUnit: fromUnit,
        formatSpeed: formatSpeed,
        formatDistance: formatDistance,
        formatDuration: formatDuration,
        haversineMeters: haversineMeters,
        bearingDeg: bearingDeg,
        pickSpeed: pickSpeed,
        isPlausibleSample: isPlausibleSample,
        SpeedSmoother: SpeedSmoother,
        isStationary: isStationary,
        STATIONARY_MPS: STATIONARY_MPS,
        MAX_PLAUSIBLE_MPS: MAX_PLAUSIBLE_MPS,
        MAX_PLAUSIBLE_ACCEL: MAX_PLAUSIBLE_ACCEL,
        ACCURACY_HARD_LIMIT: ACCURACY_HARD_LIMIT,
        R39_C: R39_C,
        r39TrueRange: r39TrueRange,
        r39IndicatedRange: r39IndicatedRange,
        calibration: calibration,
        TripAccumulator: TripAccumulator,
        TrackRecorder: TrackRecorder,
        buildGPX: buildGPX,
        buildCSV: buildCSV,
        accuracyClass: accuracyClass,
        version: '1.0.0'
    };
});
